import { array } from 'prop-types';
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import { toast } from 'react-toastify';
import { ABOUT_US, CATEGORY, COMMON, SOURCE, USER, USER_SYSTEM } from '../config/apiEndPoint';
import { GET, POST, PUT, MULTIPART_POST, MULTIPART_PUT } from 'config/api.config';

export const getDefaultUserList = async (data = array()) => {
    try {
        let url = data?._Id ? `${USER.GET_DEFAULT_USER_LIST}` : `${USER.GET_DEFAULT_USER_LIST}`;
        const response = await POST(url, data);
        return response;
    } catch (error) {
        toast.error(response.message);
    }
};

/** GET `User/system/metadata` — table columns + filter schema for Manage User list. */
export const fetchUserSystemTableAndFilterSchema = async (data = {}) => {
    try {
        const response = await GET(USER_SYSTEM.METADATA, data);
        return response;
    } catch (error) {
        toast.error(error?.message);
    }
};

/** POST `User/system/filter` — paginated user list. */
export const submitUserSystemFilterData = async (data = {}) => {
    try {
        const response = await POST(USER_SYSTEM.FILTER, data);
        return response;
    } catch (error) {
        toast.error(error?.message);
    }
};

/** GET `User/system/{userId}/client-access` — client assignment list for a user. */
export const fetchUserSystemClientAccess = async (userId = "") => {
    const raw = String(userId ?? "").trim();
    if (!raw) {
        toast.error("User id is required");
        return undefined;
    }
    try {
        const response = await GET(USER_SYSTEM.CLIENT_ACCESS(raw));
        return response;
    } catch (error) {
        toast.error(error?.message);
    }
};

/** PUT `User/system/{userId}/client-access` — update client assignments. */
export const updateUserSystemClientAccess = async (userId = "", data = {}) => {
    const raw = String(userId ?? "").trim();
    if (!raw) {
        toast.error("User id is required");
        return undefined;
    }
    try {
        const response = await PUT(USER_SYSTEM.CLIENT_ACCESS(raw), data);
        return response;
    } catch (error) {
        toast.error(error?.message);
    }
};

/** GET `User/system/{userId}/client-temp-access` — temporary client assignment list. */
export const fetchUserSystemTempClientAccess = async (userId = "") => {
    const raw = String(userId ?? "").trim();
    if (!raw) {
        toast.error("User id is required");
        return undefined;
    }
    try {
        const response = await GET(USER_SYSTEM.CLIENT_TEMP_ACCESS(raw));
        return response;
    } catch (error) {
        toast.error(error?.message);
    }
};

/** PUT `User/system/{userId}/client-temp-access` — update temporary client assignments. */
export const updateUserSystemTempClientAccess = async (userId = "", data = {}) => {
    const raw = String(userId ?? "").trim();
    if (!raw) {
        toast.error("User id is required");
        return undefined;
    }
    try {
        const response = await PUT(USER_SYSTEM.CLIENT_TEMP_ACCESS(raw), data);
        return response;
    } catch (error) {
        toast.error(error?.message);
    }
};

/** GET `User/system/{userId}/extra-permissions` — extra permission list for a user. */
export const fetchUserSystemExtraPermissions = async (userId = "") => {
    const raw = String(userId ?? "").trim();
    if (!raw) {
        toast.error("User id is required");
        return undefined;
    }
    try {
        const response = await GET(USER_SYSTEM.EXTRA_PERMISSIONS(raw));
        return response;
    } catch (error) {
        toast.error(error?.message);
    }
};

/** PUT `User/system/{userId}/extra-permissions` — update extra permissions. */
export const updateUserSystemExtraPermissions = async (userId = "", data = {}) => {
    const raw = String(userId ?? "").trim();
    if (!raw) {
        toast.error("User id is required");
        return undefined;
    }
    try {
        const response = await PUT(USER_SYSTEM.EXTRA_PERMISSIONS(raw), data);
        return response;
    } catch (error) {
        toast.error(error?.message);
    }
};

/** POST `User/system/{userId}/reset-block` — unblock a locked user account. */
export const resetUserSystemBlock = async (userId = "") => {
    const raw = String(userId ?? "").trim();
    if (!raw) {
        toast.error("User id is required");
        return undefined;
    }
    try {
        const response = await POST(USER_SYSTEM.RESET_BLOCK(raw), {});
        return response;
    } catch (error) {
        toast.error(error?.message);
    }
};

/** GET `User/{id}` — user detail (add/edit form). */
export const getUserById = async (id = "") => {
    const raw = String(id ?? "").trim();
    if (!raw) {
        toast.error("User id is required");
        return undefined;
    }
    try {
        const response = await GET(USER.BY_ID(raw));
        return response;
    } catch (error) {
        toast.error(error?.message);
    }
};

/** GET `common/work-shift` — shift options. */
export const fetchCommonWorkShiftList = async () => {
    try {
        const response = await GET(COMMON.WORK_SHIFT);
        return response;
    } catch (error) {
        toast.error(error?.message);
    }
};

/** GET `common/status` — status options for dropdowns. */
export const fetchCommonStatusList = async () => {
    try {
        const response = await GET(COMMON.STATUS);
        return response;
    } catch (error) {
        toast.error(error?.message);
    }
};

/** GET `common/user-type` — user type options for manage user add/edit. */
export const fetchCommonUserTypeList = async () => {
    try {
        const response = await GET(COMMON.USER_TYPE);
        return response;
    } catch (error) {
        toast.error(error?.message);
    }
};

const unwrapCommonListPayload = (payload) => {
    if (Array.isArray(payload)) return payload;
    const d = payload?.data ?? payload;
    if (Array.isArray(d)) return d;
    const inner = d?.records ?? d?.rows ?? d?.items;
    return Array.isArray(inner) ? inner : [];
};

/**
 * Maps `GET common/user-block-status` rows (`{ id, display }`) to react-select options.
 * `id` may be "" (All / no filter). Does not lowercase values so "false"/"true" match API.
 */
export const mapUserBlockStatusApiToSelectOptions = (payload) => {
    const list = unwrapCommonListPayload(payload);
    const mapped = list.map((item) => {
        const raw = item?.id ?? item?.value;
        const value = raw === undefined || raw === null ? "" : String(raw).trim();
        const label =
            item?.display ??
            item?.label ??
            item?.name ??
            item?.title ??
            (value !== "" ? value : "");
        return { value, label: String(label) };
    });
    if (mapped.length) return mapped;
    return [
        { value: "", label: "All" },
        { value: "false", label: "Not Blocked" },
        { value: "true", label: "Blocked" },
    ];
};

/** GET `common/user-block-status` — block / user status dropdown (filters + forms). */
export const fetchCommonUserBlockStatusList = async () => {
    try {
        const response = await GET(COMMON.USER_BLOCK_STATUS);
        return response;
    } catch (error) {
        toast.error(error?.message);
    }
};

/** POST `User` — create (`multipart/form-data`, manage user form). */
export const createUser = async (data = {}) => {
    try {
        const response = await MULTIPART_POST(USER.CREATE, data);
        return response;
    } catch (error) {
        toast.error(error?.message);
    }
};

/** PUT `User/{id}` — update (`multipart/form-data`, same fields as create). */
export const updateUser = async (id = "", data = {}) => {
    const raw = String(id ?? "").trim();
    if (!raw) {
        toast.error("User id is required");
        return undefined;
    }
    try {
        const response = await MULTIPART_PUT(USER.BY_ID(raw), data);
        return response;
    } catch (error) {
        toast.error(error?.message);
    }
};

export const addSource = async (data = array()) => {
    try {
        const response = await POST(SOURCE.ADD_SOURCE, data);
        return response;
    } catch (error) {
        toast.error(error.message);
    }
};

export const updateSource = async (id, data = array()) => {
    try {
        // in api end point page there have a contact us object where mentioned the end point of api
        const url = `${SOURCE.ADD_SOURCE}/${id}`;
        const response = await PUT(url, data);
        return response;
    } catch (error) {
        toast.error(error?.message);
    }
};

export const getSourceDataById = async (data = array()) => {
    try {
        let url = data?.Id ? `${SOURCE.GET_SOURCE_LIST}/${data?.Id}` : `${CATEGORY.GET_CATEGORY_LIST}`;
        const response = await GET(url, data);
        return response;
    } catch (error) {
        toast.error(response.message);
        // console.log(error);
    }
};

export const fetchSourceTableAndFilterSchema = async (data = array()) => {
    try {
        let url = data?._Id ? `${SOURCE.GET_JOB_SOURCE_SCHEMA}/${data?._Id}` : `${SOURCE.GET_JOB_SOURCE_SCHEMA}`;
        const response = await GET(url, data);
        return response;
    } catch (error) {
        toast.error(response.message);
        // console.log(error);
    }
};

export const submitFilterData = async (data = array()) => {
    try {
        const response = await POST(SOURCE.SUBMIT_FILTER_DATA, data);
        return response;
    } catch (error) {
        toast.error(error.message);
    }
};

